import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import React, { useCallback, useEffect, useState } from 'react';
import { Cell, Pie, PieChart, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getExpertAnalytics } from '../../redux/slices/analytics/experts';
import { getErrorInsightStages } from '../../redux/slices/analytics/configAnalytics';
import { STATUS } from '../../utils/status';
import api from '../../utils/api';
import TaskStatusModal from '../outputJson/taskStatusModal';
import SnackbarNotification from '../snackbar/snackbarNotification';
import { usePollingTaskStatus } from '../outputJson/usePollingStatus';
import { getOutputJsonTaskStatus, setTaskStatus } from '../../redux/slices/outputJson';

/**
 * Parses the API error_type string into clean label strings.
 * The field can arrive in several forms:
 *   - null / undefined
 *   - "['all changed'], ['Incorrect data']"  (comma-separated list-reprs)
 *   - "['all changed']"                       (single list-repr)
 *   - "[]"                                    (empty list-repr)
 *   - "typos"                                 (plain string)
 *   - "typos, all changed"                    (plain comma-separated)
 */
const parseErrorTypes = (errorType: string | null | undefined): string[] => {
  if (!errorType) return [];
  const trimmed = errorType.trim();
  if (!trimmed) return [];

  // Extract all [...] segments and parse each one
  const bracketMatches = trimmed.match(/\[([^\]]*)\]/g);
  if (bracketMatches) {
    return bracketMatches.flatMap((match) => {
      const inner = match.slice(1, -1).trim(); // strip [ ]
      if (!inner) return []; // [] → skip
      return inner
        .split(',')
        .map((s) => s.trim().replace(/^['"]|['"]$/g, '').trim())
        .filter(Boolean);
    });
  }

  // No brackets — plain comma-separated
  return trimmed.split(',').map((t) => t.trim()).filter(Boolean);
};

const formatErrorTypeLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const words = trimmed.split(/\s+/);
  return words
    .map((word, index) => {
      const lowerWord = word.toLowerCase();
      if (lowerWord === 'ai') return 'AI';
      if (index === 0) return lowerWord.charAt(0).toUpperCase() + lowerWord.slice(1);
      return lowerWord;
    })
    .join(' ');
};

export const aggregateErrorTypeLabels = (errorType: string | null): Array<{ label: string; count: number; displayLabel: string }> => {
  const orderedMap = new Map<string, { label: string; count: number }>();

  parseErrorTypes(errorType).forEach((type) => {
    const normalizedKey = type.trim().toLowerCase();
    if (!normalizedKey) return;

    const existing = orderedMap.get(normalizedKey);
    if (existing) {
      existing.count += 1;
      return;
    }

    orderedMap.set(normalizedKey, {
      label: formatErrorTypeLabel(type),
      count: 1,
    });
  });

  return Array.from(orderedMap.entries()).map(([_, item]) => ({
    ...item,
    displayLabel: item.count > 1 ? `${item.label}(${item.count})` : item.label,
  }));
};

/**
 * Normalises the error_type_breakdown array from the summary API:
 * - Parses bracket-quoted labels to clean text
 * - Removes entries where count === 0
 * - Removes entries whose parsed label is empty (was "[]")
 * - Deduplicates by normalized label, summing counts
 * - Re-sorts by count descending
 */
const normalizeErrorBreakdown = (
  items: Array<{ error_type: string; count: number; percentage: number }>
): Array<{ label: string; count: number; percentage: number }> => {
  const map = new Map<string, { count: number; percentage: number }>();

  for (const item of items) {
    if (item.count === 0) continue;
    const labels = parseErrorTypes(item.error_type);
    if (labels.length === 0) continue; // was "[]"
    // Use joined label as the display key (handles multi-value rows)
    const key = labels.join(', ');
    const existing = map.get(key);
    if (existing) {
      existing.count += item.count;
      existing.percentage += item.percentage;
    } else {
      map.set(key, { count: item.count, percentage: item.percentage });
    }
  }

  return Array.from(map.entries())
    .map(([label, stats]) => ({ label, ...stats }))
    .sort((a, b) => b.count - a.count);
};

const PIE_COLORS = [
  '#DC2626', '#F97316', '#EAB308', '#22C55E',
  '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4',
  '#84CC16', '#F43F5E',
];

const TASK_PAGE_SIZE = 10;

interface ConfigColumn {
  config_id: string;
  title: string;
  name?: string;
  identifier?: string;
}

interface TaskRow {
  task_id: string;
  task_name: string;
  config_scores: Record<string, number | null>;
  total_score: number | null;
  error_type: string | null;
}

interface TaskPagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

const ErrorInsight = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const association_type = useSelector((state: RootState) => state.projects.association_type);
  const analyticsData = useSelector((state: RootState) => state.analyticsExpert.analyticsExpert);
  const errorInsightStages = useSelector((state: RootState) => state.configAnalytics.errorInsightStages);
  const getErrorInsightStagesStatus = useSelector((state: RootState) => state.configAnalytics.getErrorInsightStagesStatus);

  const { getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse } = useSelector(
    (state: RootState) => state.outputJson
  );

  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [overviewExpanded, setOverviewExpanded] = useState(true);

  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [openTaskStatusModal, setOpenTaskStatusModal] = useState(false);
  const [exportCeleryTaskId, setExportCeleryTaskId] = useState<string | undefined>(undefined);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Task-level table state
  const [scoringEnabled, setScoringEnabled] = useState<boolean | null>(null);
  const [configColumns, setConfigColumns] = useState<ConfigColumn[]>([]);
  const [taskRows, setTaskRows] = useState<TaskRow[]>([]);
  const [taskPagination, setTaskPagination] = useState<TaskPagination | null>(null);
  const [taskPage, setTaskPage] = useState(1);
  const [taskTableStatus, setTaskTableStatus] = useState<STATUS>(STATUS.NOT_STARTED);

  // Summary cards derived from expert analytics
  const acceptedPercentage = Number(analyticsData?.accepted_percentage ?? 0);
  const acceptedCount = Number(analyticsData?.accepted_count ?? 0);
  const rejectedPercentage = Number(analyticsData?.rejected_percentage ?? 0);
  const rejectedCount = Number(analyticsData?.rejected_count ?? 0);
  const totalReviewed = Number(analyticsData?.total_reviewed ?? 0);
  const rawErrorTypes = analyticsData?.error_type_breakdown || [];
  const topErrorTypes = normalizeErrorBreakdown(rawErrorTypes);

  // Fetch stage pills
  useEffect(() => {
    if (id && association_type) {
      dispatch(getErrorInsightStages({ project_id: id, association_type }));
    }
  }, [dispatch, id, association_type]);

  // Auto-select first stage
  useEffect(() => {
    if (errorInsightStages.length > 0 && !selectedStageId) {
      setSelectedStageId(String(errorInsightStages[0].id));
    }
  }, [errorInsightStages, selectedStageId]);

  // Fetch summary cards per stage
  useEffect(() => {
    if (id && selectedStageId) {
      dispatch(
        getExpertAnalytics({
          project_id: id,
          tab: 'error_insights',
          association_type,
          stage_id: selectedStageId,
        })
      );
    }
  }, [dispatch, id, association_type, selectedStageId]);

  // Poll for queued export task status
  usePollingTaskStatus({
    status: taskStatusOutputJsonResponse?.data?.status,
    taskId: exportCeleryTaskId,
  });

  // Open modal when polling starts
  useEffect(() => {
    const status = taskStatusOutputJsonResponse?.data?.status;
    if ((status === 'PENDING' || status === 'SUCCESS' || status === 'FAILURE') && exportCeleryTaskId) {
      setOpenTaskStatusModal(true);
    }
  }, [taskStatusOutputJsonResponse, exportCeleryTaskId]);

  // Handle export task completion
  useEffect(() => {
    if (getTaskStatusOutputJson === STATUS.SUCESS && taskStatusOutputJsonResponse?.data?.url && exportCeleryTaskId) {
      const url = taskStatusOutputJsonResponse.data.url as string;
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = url.split('/').pop()?.split('?')[0] || 'error-insights-export';
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {
          setSnackbarMessage('Failed to download error insights export.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        });
    }

    if (getTaskStatusOutputJson === STATUS.FAILED && exportCeleryTaskId) {
      setSnackbarMessage(getTaskStatusOutputJsonMessage || 'Error insights export failed.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    return () => {
      if (getTaskStatusOutputJson === STATUS.SUCESS || getTaskStatusOutputJson === STATUS.FAILED) {
        dispatch(setTaskStatus(STATUS.NOT_STARTED));
        setExportCeleryTaskId(undefined);
      }
    };
  }, [dispatch, getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse, exportCeleryTaskId]);

  const handleExport = async () => {
    if (!id) return;
    setExportLoading(true);
    try {
      const response = await api.get('project/qa-analytics/error-insights/export/', {
        params: { project_id: id },
      });
      const data = response.data?.data;
      if (data?.status === 'ready' && data?.url) {
        fetch(data.url as string)
          .then((res) => res.blob())
          .then((blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = (data.url as string).split('/').pop()?.split('?')[0] || 'error-insights-export';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(() => {
            setSnackbarMessage('Failed to download export file.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      } else if (data?.status === 'pending' && data?.bg_task_id) {
        const celeryId = data.bg_task_id as string;
        setExportCeleryTaskId(celeryId);
        dispatch(getOutputJsonTaskStatus({ celery_task_id: celeryId }));
        setOpenTaskStatusModal(true);
      } else {
        setSnackbarMessage('Unexpected export response. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      const httpStatus = error?.response?.status;
      if (httpStatus === 400) {
        setSnackbarMessage('No data available to export for this project.');
      } else if (httpStatus === 404) {
        setSnackbarMessage('Project not found.');
      } else if (httpStatus === 403) {
        setSnackbarMessage('You are not authorized to export this data.');
      } else {
        setSnackbarMessage('Export failed. Please try again later.');
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch task-level table
  const fetchTaskData = useCallback(() => {
    if (!id || !selectedStageId) return;
    setTaskTableStatus(STATUS.PENDING);
    api
      .get('project/qa-analytics/overview/', {
        params: {
          tab: 'error_insights_tasks',
          project_id: id,
          stage_id: selectedStageId,
          page: taskPage,
          page_size: TASK_PAGE_SIZE,
        },
      })
      .then((response) => {
        const data = response.data?.data;
        setScoringEnabled(data?.scoring_enabled ?? false);
        setConfigColumns(data?.config_columns ?? []);
        setTaskRows(data?.tasks ?? []);
        setTaskPagination(data?.pagination ?? null);
        setTaskTableStatus(STATUS.SUCESS);
      })
      .catch(() => {
        setTaskTableStatus(STATUS.FAILED);
      });
  }, [id, selectedStageId, taskPage]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const handleStageChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setSelectedStageId(newValue);
      setTaskPage(1);
      setTaskRows([]);
      setConfigColumns([]);
      setTaskPagination(null);
      setScoringEnabled(null);
      setTaskTableStatus(STATUS.NOT_STARTED);
      setOverviewExpanded(true);
    }
  };

  const handleOverviewAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
    setOverviewExpanded(expanded);
  };

  const renderScore = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    return String(value);
  };

  const renderErrorType = (errorType: string | null): React.ReactNode => {
    const types = aggregateErrorTypeLabels(errorType);
    if (types.length === 0) {
      return <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>—</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {types.map((type) => (
          <Box
            key={`${type.label}-${type.count}`}
            component="span"
            sx={{
              backgroundColor: '#FEF3C7',
              color: '#92400E',
              borderRadius: '20px',
              px: 1.5,
              py: 0.25,
              fontSize: '11px',
              fontWeight: 600,
              fontFamily: 'Poppins',
              whiteSpace: 'nowrap',
            }}
          >
            {type.displayLabel}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {/* Stage pills + Export button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
        <Box>
          {getErrorInsightStagesStatus === STATUS.PENDING && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>Loading stages...</Typography>
            </Box>
          )}

          {getErrorInsightStagesStatus === STATUS.SUCESS && errorInsightStages.length > 0 && (
            <Box
              sx={{
                backgroundColor: '#FFFFFF',
                border: '1px solid lightgrey',
                borderRadius: '15px',
                padding: '5px',
                display: 'inline-flex',
              }}
            >
              <ToggleButtonGroup
                color="standard"
                value={selectedStageId}
                exclusive
                onChange={handleStageChange}
              >
                {errorInsightStages.map((stage) => (
                  <ToggleButton
                    key={stage.id}
                    value={String(stage.id)}
                    sx={{
                      border: 'none !important',
                      fontFamily: 'Montserrat !important',
                      fontSize: '14px !important',
                      fontWeight: '600 !important',
                      lineHeight: '24px !important',
                      color: selectedStageId === String(stage.id) ? '#fff !important' : '#6C6C6C !important',
                      borderRadius: '15px !important',
                      backgroundColor:
                        selectedStageId === String(stage.id) ? '#03BBD2 !important' : '#fff !important',
                      textTransform: 'none !important',
                    }}
                  >
                    {stage.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={exportLoading}
          startIcon={exportLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <DownloadIcon />}
          sx={{
            textTransform: 'none',
            fontFamily: 'Poppins',
            fontSize: '13px',
            fontWeight: 600,
            backgroundColor: '#03BBD2',
            borderRadius: '8px',
            px: 2,
            '&:hover': { backgroundColor: '#029DB8' },
            '&.Mui-disabled': { backgroundColor: '#9CA3AF', color: 'white' },
          }}
        >
          {exportLoading ? 'Exporting...' : 'Export'}
        </Button>
      </Box>

      {/* Stage content */}
      {selectedStageId && (
        <TableContainer sx={{ mt: 0 }}>
          <Box
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '16px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
              padding: '8px',
            }}
          >
            <Accordion
              expanded={overviewExpanded}
              onChange={handleOverviewAccordionChange}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '12px !important',
                overflow: 'hidden',
                backgroundColor: '#FCFDFD',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="error-insight-overview-content"
                id="error-insight-overview-header"
                sx={{
                  flexDirection: 'row-reverse',
                  minHeight: '56px',
                  padding: '0 14px',
                  backgroundColor: '#F8FAFC',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: '8px', color: '#03BBD2' },
                  '& .MuiAccordionSummary-content': { margin: '12px 0' },
                }}
              >
                <Typography sx={{ fontWeight: 600, fontFamily: 'Poppins', color: '#111827' }}>
                  Overview
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ padding: '20px', backgroundColor: '#FAFAFA' }}>
                {/* ── Summary cards ── */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  {/* ACCEPTED card */}
                  <Card sx={{ borderRadius: '12px', border: '1px solid #E9EEF4' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, height: '100%' }}>
                      {/* Donut */}
                      <Box sx={{ flexShrink: 0, width: 90, height: 90, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { value: acceptedPercentage },
                                { value: Math.max(0, 100 - acceptedPercentage) },
                              ]}
                              dataKey="value"
                              cx="50%"
                              cy="50%"
                              innerRadius={28}
                              outerRadius={40}
                              startAngle={90}
                              endAngle={-270}
                              strokeWidth={0}
                            >
                              <Cell fill="#22C55E" />
                              <Cell fill="#DCFCE7" />
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <Box
                          sx={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#15803D' }}>
                            {acceptedPercentage}%
                          </Typography>
                        </Box>
                      </Box>
                      {/* Text */}
                      <Box>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#5F6B7A', mb: 0.5, textTransform: 'uppercase' }}>
                          Accepted
                        </Typography>
                        <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#15803D', lineHeight: 1 }}>
                          {acceptedPercentage}%
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                          {acceptedCount} / {totalReviewed} Tasks
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* REJECTED card */}
                  <Card sx={{ borderRadius: '12px', border: '1px solid #E9EEF4' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, height: '100%' }}>
                      {/* Donut */}
                      <Box sx={{ flexShrink: 0, width: 90, height: 90, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { value: rejectedPercentage },
                                { value: Math.max(0, 100 - rejectedPercentage) },
                              ]}
                              dataKey="value"
                              cx="50%"
                              cy="50%"
                              innerRadius={28}
                              outerRadius={40}
                              startAngle={90}
                              endAngle={-270}
                              strokeWidth={0}
                            >
                              <Cell fill="#EF4444" />
                              <Cell fill="#FEE2E2" />
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <Box
                          sx={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#B91C1C' }}>
                            {rejectedPercentage}%
                          </Typography>
                        </Box>
                      </Box>
                      {/* Text */}
                      <Box>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#5F6B7A', mb: 0.5, textTransform: 'uppercase' }}>
                          Rejected
                        </Typography>
                        <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#B91C1C', lineHeight: 1 }}>
                          {rejectedPercentage}%
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.5 }}>
                          {rejectedCount} / {totalReviewed} Tasks
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  <Card sx={{ borderRadius: '12px', border: '1px solid #E9EEF4' }}>
                    <CardContent>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#5F6B7A', mb: 1.5 }}>
                        TOP ERROR TYPES
                      </Typography>
                      {topErrorTypes.length === 0 ? (
                        <Typography sx={{ fontSize: '12px', color: '#64748B' }}>No error data available</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          {/* Donut chart */}
                          <Box sx={{ flexShrink: 0, width: 120, height: 120 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={topErrorTypes}
                                  dataKey="count"
                                  nameKey="label"
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={32}
                                  outerRadius={52}
                                  paddingAngle={2}
                                  strokeWidth={0}
                                >
                                  {topErrorTypes.map((_, idx) => (
                                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                  ))}
                                </Pie>
                                <RechartsTooltip
                                  formatter={(value: number, name: string) => [
                                    `${value} tasks`,
                                    name,
                                  ]}
                                  contentStyle={{
                                    fontSize: '11px',
                                    fontFamily: 'Poppins',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E7EB',
                                  }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>

                          {/* Legend */}
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {topErrorTypes.map((item, index) => (
                              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    color: '#374151',
                                    fontFamily: 'Poppins',
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={item.label}
                                >
                                  {item.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    color: '#374151',
                                    fontFamily: 'Poppins',
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.count} <Box component="span" sx={{ fontWeight: 400, color: '#6B7280' }}>({item.percentage}%)</Box>
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>

                {/* ── Task-level scoring table ── */}
                <Box
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  {/* Table heading */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid #E5E7EB',
                      backgroundColor: '#F8FAFC',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#374151',
                        fontFamily: 'Poppins',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Task-Wise Score Details
                    </Typography>
                    {taskPagination && (
                      <Typography sx={{ fontSize: '11px', color: '#6B7280', fontFamily: 'Poppins' }}>
                        Showing {((taskPagination.page - 1) * taskPagination.page_size) + 1}–
                        {Math.min(taskPagination.page * taskPagination.page_size, taskPagination.total)} of{' '}
                        {taskPagination.total} entries
                      </Typography>
                    )}
                  </Box>

                  {/* Loading */}
                  {taskTableStatus === STATUS.PENDING && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                      <CircularProgress size={28} sx={{ color: '#03BBD2' }} />
                    </Box>
                  )}

                  {/* Scoring not enabled */}
                  {taskTableStatus === STATUS.SUCESS && scoringEnabled === false && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography sx={{ fontSize: '13px', color: '#6B7280', fontFamily: 'Poppins' }}>
                        Scoring is not enabled for this project.
                      </Typography>
                    </Box>
                  )}

                  {/* Error */}
                  {taskTableStatus === STATUS.FAILED && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography sx={{ fontSize: '13px', color: '#EF4444', fontFamily: 'Poppins' }}>
                        Failed to load task data. Please try again.
                      </Typography>
                    </Box>
                  )}

                  {/* Table */}
                  {taskTableStatus === STATUS.SUCESS && scoringEnabled === true && (
                    <>
                      <TableContainer>
                        <Table sx={{ minWidth: 700 }} aria-label="error insight task scoring table">
                          <TableHead>
                            {/* Top header row */}
                            <TableRow sx={{ backgroundColor: '#F7F9FA' }}>
                              <TableCell
                                rowSpan={configColumns.length > 0 ? 2 : 1}
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  fontFamily: 'Poppins',
                                  color: '#374151',
                                  borderRight: '1px solid #E5E7EB',
                                  minWidth: 160,
                                  verticalAlign: 'middle',
                                }}
                              >
                                TASK NAME
                              </TableCell>

                              {configColumns.length > 0 && (
                                <TableCell
                                  colSpan={configColumns.length}
                                  align="center"
                                  sx={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    fontFamily: 'Poppins',
                                    color: '#374151',
                                    borderRight: '1px solid #E5E7EB',
                                    backgroundColor: '#EFF6FF',
                                  }}
                                >
                                  CONFIG SCORE DETAILS (Per Task)
                                </TableCell>
                              )}

                              <TableCell
                                rowSpan={configColumns.length > 0 ? 2 : 1}
                                align="center"
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  fontFamily: 'Poppins',
                                  color: '#374151',
                                  borderRight: '1px solid #E5E7EB',
                                  minWidth: 130,
                                  verticalAlign: 'middle',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                TOTAL SCORE
                                <Typography
                                  component="span"
                                  sx={{ display: 'block', fontSize: '10px', fontWeight: 400, color: '#9CA3AF' }}
                                >
                                  (Avg)
                                </Typography>
                              </TableCell>

                              <TableCell
                                rowSpan={configColumns.length > 0 ? 2 : 1}
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  fontFamily: 'Poppins',
                                  color: '#374151',
                                  borderRight: '1px solid #E5E7EB',
                                  minWidth: 180,
                                  verticalAlign: 'middle',
                                }}
                              >
                                ERROR TYPE SELECTED
                                <Typography
                                  component="span"
                                  sx={{ display: 'block', fontSize: '10px', fontWeight: 400, color: '#9CA3AF' }}
                                >
                                  (by Reviewer)
                                </Typography>
                              </TableCell>

                              <TableCell
                                rowSpan={configColumns.length > 0 ? 2 : 1}
                                align="center"
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  fontFamily: 'Poppins',
                                  color: '#374151',
                                  minWidth: 60,
                                  verticalAlign: 'middle',
                                }}
                              >
                                VIEW
                              </TableCell>
                            </TableRow>

                            {/* Sub-header row with individual config column names */}
                            {configColumns.length > 0 && (
                              <TableRow sx={{ backgroundColor: '#F7F9FA' }}>
                                {configColumns.map((col) => (
                                  <TableCell
                                    key={col.config_id}
                                    align="center"
                                    sx={{
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      fontFamily: 'Poppins',
                                      color: '#6B7280',
                                      borderRight: '1px solid #E5E7EB',
                                      py: 1,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                      <span>{col.name || col.title}</span>
                                      {col.identifier && (
                                        <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 400 }}>{col.identifier}</span>
                                      )}
                                    </Box>
                                  </TableCell>
                                ))}
                              </TableRow>
                            )}
                          </TableHead>

                          <TableBody>
                            {taskRows.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={3 + configColumns.length + 1}
                                  sx={{ textAlign: 'center', py: 5 }}
                                >
                                  <Typography sx={{ fontSize: '13px', color: '#9CA3AF', fontFamily: 'Poppins' }}>
                                    No task data available for this stage yet.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              taskRows.map((row) => (
                                <TableRow
                                  key={row.task_id}
                                  sx={{
                                    '&:hover': { backgroundColor: '#F9FAFB' },
                                    '&:last-child td': { borderBottom: 0 },
                                  }}
                                >
                                  {/* Task Name */}
                                  <TableCell
                                    sx={{
                                      fontSize: '13px',
                                      fontFamily: 'Poppins',
                                      fontWeight: 500,
                                      color: '#1D4ED8',
                                      borderRight: '1px solid #E5E7EB',
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' },
                                    }}
                                    onClick={() => navigate(`/project/${id}?tab=task&openTaskId=${row.task_id}`)}
                                  >
                                    {row.task_name || '—'}
                                  </TableCell>

                                  {/* Config score cells */}
                                  {configColumns.map((col) => {
                                    const score = row.config_scores?.[col.config_id];
                                    const hasScore = score !== null && score !== undefined;
                                    return (
                                      <TableCell
                                        key={col.config_id}
                                        align="center"
                                        sx={{
                                          fontSize: '13px',
                                          fontFamily: 'Poppins',
                                          fontWeight: hasScore ? 600 : 400,
                                          color: hasScore ? '#111827' : '#9CA3AF',
                                          borderRight: '1px solid #E5E7EB',
                                        }}
                                      >
                                        {renderScore(score)}
                                      </TableCell>
                                    );
                                  })}

                                  {/* Total Score */}
                                  <TableCell align="center" sx={{ borderRight: '1px solid #E5E7EB' }}>
                                    {row.total_score !== null && row.total_score !== undefined ? (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-block',
                                          px: 1.5,
                                          py: 0.5,
                                          borderRadius: '20px',
                                          fontSize: '13px',
                                          fontWeight: 700,
                                          fontFamily: 'Poppins',
                                          backgroundColor:
                                            row.total_score >= 4
                                              ? '#DCFCE7'
                                              : row.total_score >= 2.5
                                              ? '#FEF9C3'
                                              : '#FEE2E2',
                                          color:
                                            row.total_score >= 4
                                              ? '#15803D'
                                              : row.total_score >= 2.5
                                              ? '#92400E'
                                              : '#B91C1C',
                                        }}
                                      >
                                        {row.total_score}
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>—</Typography>
                                    )}
                                  </TableCell>

                                  {/* Error Type */}
                                  <TableCell sx={{ borderRight: '1px solid #E5E7EB' }}>
                                    {renderErrorType(row.error_type)}
                                  </TableCell>

                                  {/* View icon */}
                                  <TableCell align="center">
                                    <Tooltip title="View task details" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => navigate(`/project/${id}?tab=task&openTaskId=${row.task_id}`)}
                                        sx={{
                                          color: '#03BBD2',
                                          '&:hover': { backgroundColor: '#E0F7FA' },
                                        }}
                                      >
                                        <VisibilityOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      {taskPagination && taskPagination.total_pages > 1 && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            borderTop: '1px solid #E5E7EB',
                          }}
                        >
                          <Pagination
                            count={taskPagination.total_pages}
                            page={taskPage}
                            onChange={(_event, page) => setTaskPage(page)}
                            color="primary"
                            shape="rounded"
                            size="small"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TableContainer>
      )}
    </>
  );
};

export default ErrorInsight;
